import memoizeOne from "memoize-one";
import queryString from "query-string";
import { useEffect } from "react";
import { type RouteComponentProps } from "react-router-dom";

import { changeOrdersCreateDraftByJobIdPostV2 } from "@btnet/services-btnet/handlers/change-orders/change-orders";

import { OpenedFrom } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { lazyLoadWithRetry } from "utilities/react.utilities";
import { routes } from "utilities/routes";

import { type IOptionalModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { withJobPrompt } from "commonComponents/helpers/JobPrompt/JobPrompt";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";
import { PortalInfoContext } from "commonComponents/utilities/InlineOwnerPreview/PortalInfoContext";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import { type IChangeOrderProps } from "entity/changeOrder/ChangeOrder/ChangeOrder";
import { InlinePreviewChangeOrderHandler } from "entity/changeOrder/ChangeOrder/ChangeOrder.api.handler";
import { type ChangeOrderEntity } from "entity/changeOrder/ChangeOrder/ChangeOrder.api.types";

const ChangeOrder = lazyLoadWithRetry(() => import("./ChangeOrder"));

interface IChangeOrderDraftCreatorProps
    extends IOptionalModalConfiguration,
        RouteComponentProps<{ jobId: string }> {
    jobId: number;
}

const ChangeOrderDraftCreatorInternal = ({ jobId, history }: IChangeOrderDraftCreatorProps) => {
    useEffect(() => {
        const createDraft = async () => {
            try {
                const result = await changeOrdersCreateDraftByJobIdPostV2(jobId);
                history.replace(
                    routes.changeOrder.getChangeOrderDetailsFullPage(
                        result.data.changeOrderId,
                        jobId,
                    ),
                );
            } catch (e) {
                showApixErrorMessage(e as Error);
                history.replace(routes.changeOrder.getListLink());
            }
        };
        void createDraft();
    }, [jobId, history]);

    return <BTLoading />;
};

const ChangeOrderDraftCreator = withJobPrompt(ChangeOrderDraftCreatorInternal, {
    routeTransform: (props) => props.match.url.replace(/\/\d+$/, `/${props.jobId}`),
});

export const RouteChangeOrderQuickAdd = (props: IOptionalModalConfiguration) => (
    <RouteRelative
        path="/ChangeOrderDraft/:jobId"
        render={(routeProps: RouteComponentProps<{ jobId: string }>) => (
            <ChangeOrderDraftCreator
                {...routeProps}
                jobId={parseInt(routeProps.match.params.jobId)}
                modalConfig={props.modalConfig}
            />
        )}
    />
);

const PreviewChangeOrderHandler = memoizeOne((changeOrder: ChangeOrderEntity) => {
    return new InlinePreviewChangeOrderHandler(changeOrder);
});

export const RouteChangeOrder = (
    props: Omit<IChangeOrderProps, "id" | "jobId" | "history" | "location" | "match">,
) => (
    <RouteRelative
        path={routes.changeOrder.changeOrderDetails}
        render={(routeProps) => {
            // eslint-disable-next-line deprecate/member-expression
            const qsValues: any = queryString.parse(routeProps.location.search, {
                parseBooleans: true,
            });
            const urlParams = routeProps.match.params;

            const coId = parseInt(urlParams.id);
            const jobId = parseInt(urlParams.jobId);
            let openedFrom = props.openedFrom ? props.openedFrom : OpenedFrom.Nothing;
            if (qsValues.fromBid) {
                openedFrom = OpenedFrom.Bid;
            }
            if (qsValues.fromRFI) {
                openedFrom = OpenedFrom.Rfi;
            }

            return (
                <BTSuspense>
                    <PortalInfoContext.Consumer>
                        {(portalInfo) => {
                            let handler;
                            if (portalInfo.currentPortal !== portalInfo.rootPortal) {
                                handler = PreviewChangeOrderHandler(props.changeOrder!);
                            }
                            return (
                                <ChangeOrder
                                    {...props}
                                    {...routeProps}
                                    id={coId}
                                    jobId={jobId}
                                    openedFrom={openedFrom}
                                    handler={handler}
                                />
                            );
                        }}
                    </PortalInfoContext.Consumer>
                </BTSuspense>
            );
        }}
    />
);
